import { getLoginChallenge } from '../service/api-actions.js';
import { getSignature, getWalletPublicKey } from '../helper/wallet-helper.js';

/**
 * Build the wallet proof-of-possession that /login requires — and NOTHING else.
 *
 * An NCOG wallet holds two keypairs. The ML-KEM ("data") key is the account identity this app already
 * sends as `publicKey`, but it cannot produce a signature. Only the ML-DSA ("asset") key can sign, so the
 * proof is: fetch a single-use server challenge, sign it with ncog_personalSign, and send the signature
 * together with the ML-DSA public key as a SEPARATE `signerPublicKey` field. The backend verifies the
 * signature, derives keccak256(signerPublicKey)[12:] and requires it to equal the account's on-chain
 * "assetAddress" attribute (written at signup — see routes/Signup.js).
 *
 * This is unconditional by design. Accounts created before the attribute existed WILL be rejected with
 * "Wallet does not match this account" and must be re-created; there is no unsigned fallback, because
 * falling back would defeat the proof entirely.
 *
 * It deliberately does NOT call login() itself: every call site has its own coupon code, cookie/cache
 * handling and navigation, so mixing the two responsibilities here only hid what each screen actually
 * does. Callers build the proof, then call login(user_name, publicKey, couponCode, signature,
 * signerPublicKey) themselves.
 *
 * Failures THROW rather than returning a result object — a missing proof is never a login the caller
 * should be able to attempt, and an accidentally-ignored return value would do exactly that. The Error
 * message is already user-facing, so call sites can toast `error.message` unchanged.
 *
 * @returns {Promise<{ signature: string, signerPublicKey: string }>}
 */
export async function buildLoginProof(userName, publicKey) {
    const challengeResponse = await getLoginChallenge(userName, publicKey);
    const challenge = challengeResponse?.challenge;
    if (!challenge) {
        throw new Error(challengeResponse?.message || 'Could not start a secure login. Please try again.');
    }

    const signature = await getSignature(challenge);
    const signerPublicKey = await getWalletPublicKey();
    if (!signature || !signerPublicKey) {
        throw new Error('Wallet signature is required to sign in. Please approve the request in your NCOG wallet and try again.');
    }

    return { signature, signerPublicKey };
}
